import { cropPixelImage, pixelOffset } from './image.js';
import { buildLineAnchors, CHARSET_PRINTABLE_ASCII, composeGlyphSequenceWeights, compileDecodeToken, decodeLine, decodeLineRightToLeft, evidenceAt, getDefaultGlyphAtlas, GLYPH_HEIGHT, textInkProfile, } from './index-core.js';
/**
 * MapleStory uses two periods as a terminal truncation marker in narrow cells.
 * The period glyph has no exact anchor pixels, so this token requires a full
 * soft-shape match and terminates decoding as soon as the pair is recognized.
 */
export const TRAILING_DOUBLE_DOT_TOKEN = Object.freeze({
    matchText: '..',
    terminate: true,
    trailingOnly: true,
    trailingTolerance: 1,
    minShapeScore: 0.55,
});
const DEFAULT_AUTO_PROFILES = ['black', 'red'];
function evidenceBounds(image, profile, threshold) {
    let x0 = Infinity, y0 = Infinity, x1 = -1, y1 = -1, score = 0, pixels = 0;
    for (let y = 0; y < image.height; y++)
        for (let x = 0; x < image.width; x++) {
            const i = pixelOffset(image, x, y);
            const ev = profile.evidencePixel(image.data[i] ?? 0, image.data[i + 1] ?? 0, image.data[i + 2] ?? 0);
            if (ev < threshold)
                continue;
            score += ev;
            pixels++;
            x0 = Math.min(x0, x);
            y0 = Math.min(y0, y);
            x1 = Math.max(x1, x);
            y1 = Math.max(y1, y);
        }
    return pixels ? { x0, y0, x1: x1 + 1, y1: y1 + 1, score, pixels } : null;
}
function chooseProfile(image, option, threshold) {
    if (option && option !== 'auto') {
        const profile = textInkProfile(option);
        return { profile, bounds: evidenceBounds(image, profile, threshold) };
    }
    let best;
    for (const name of DEFAULT_AUTO_PROFILES) {
        const profile = textInkProfile(name), bounds = evidenceBounds(image, profile, threshold);
        if (bounds && (!best || bounds.score > best.bounds.score))
            best = { profile, bounds };
    }
    return best ?? { profile: textInkProfile('black'), bounds: null };
}
function textLineRect(image, bounds) {
    const y = Math.min(Math.max(0, image.height - GLYPH_HEIGHT), Math.max(0, bounds.y0 - 1));
    return { x: 0, y, width: image.width, height: Math.min(GLYPH_HEIGHT, image.height - y) };
}
function atlasFor(variant, charset) {
    return getDefaultGlyphAtlas({ variant, charset });
}
function charsetWithTokens(charset, options) {
    const chars = new Set(charset);
    for (const text of options.tokenTexts ?? [])
        for (const ch of text)
            chars.add(ch);
    for (const spec of options.tokenSpecs ?? [])
        for (const ch of spec.matchText)
            chars.add(ch);
    return [...chars].join('');
}
function tokens(atlas, texts, specs) {
    const out = (texts ?? []).map((text) => compileDecodeToken(atlas, text, { acceptOnAnchorMatch: true }));
    for (const spec of specs ?? []) {
        out.push(compileDecodeToken(atlas, spec.matchText, {
            text: spec.outputText ?? spec.matchText,
            acceptOnAnchorMatch: spec.acceptOnAnchorMatch,
            terminate: spec.terminate,
            trailingOnly: spec.trailingOnly,
            trailingTolerance: spec.trailingTolerance,
            minShapeScore: spec.minShapeScore,
        }));
    }
    return out.sort((a, b) => b.anchorKey.length - a.anchorKey.length);
}
function clean(text) { return text.trim(); }
function quality(result) {
    const value = clean(result.text);
    if (!value)
        return -Infinity;
    let penalty = /^\s/u.test(result.text) ? 8 : 0;
    if (/^[,|']/u.test(value))
        penalty += 16;
    if (/'+$/u.test(result.text.trim()))
        penalty += 1;
    return value.length * 10 + result.score - penalty;
}
function endedWithTerminalToken(result) {
    const decision = result.decisions.at(-1);
    return decision?.kind === 'token' && decision.selected.token.terminate === true;
}
function decodeWithAtlas(atlas, variant, line, lineRect, options) {
    if (options.direction === 'right-to-left') {
        if ((options.tokenTexts?.length ?? 0) > 0 || (options.tokenSpecs?.length ?? 0) > 0) {
            throw new Error('right-to-left decoding does not support multi-glyph tokens');
        }
        const result = decodeLineRightToLeft(atlas, line, {
            startX: options.startX,
            endX: options.fixedCropBounds ? line.width : options.endX ?? line.estimatedEndX,
            beamSize: options.beamSize,
            branchLimit: options.branchLimit,
            confidenceFloor: options.confidenceFloor,
            minAnchorlessShapeScore: options.minAnchorlessShapeScore,
            denylist: options.denylist,
            useOverhangEvidence: options.useOverhangEvidence,
        });
        return {
            text: options.trim === false ? result.text : clean(result.text),
            rawText: result.text,
            result,
            line,
            lineRect,
            profile: line.profile,
            atlasVariant: variant,
        };
    }
    const pad = options.startSearchPad ?? 3;
    const startXs = options.fixedCropBounds
        ? [0]
        : options.startX === undefined
            ? Array.from({ length: line.estimatedStartX + 1 - Math.max(0, line.estimatedStartX - pad) + 1 }, (_, index) => Math.max(0, line.estimatedStartX - pad) + index)
            : [options.startX];
    let best;
    const tokenList = tokens(atlas, options.tokenTexts, options.tokenSpecs);
    const hasTerminalToken = tokenList.some((token) => token.terminate);
    for (const x of startXs) {
        // First decode to the ordinary exact-anchor-derived end. Merely enabling a
        // terminal token must not make an untruncated line consume anchorless
        // glyphs from the soft-ink tail.
        const normal = decodeLine(atlas, line, {
            startX: x,
            endX: options.endX,
            beamSize: options.beamSize,
            branchLimit: options.branchLimit,
            confidenceFloor: options.confidenceFloor,
            tokenList,
            denylist: options.denylist,
            useOverhangEvidence: options.useOverhangEvidence,
            useBinaryMaskPruning: options.useBinaryMaskPruning,
        });
        if (!best || quality(normal) > quality(best))
            best = normal;
        // A punctuation-only truncation marker has no exact anchors, so it may sit
        // past the normal end estimate. Run one extended attempt, but keep it only
        // when it actually finishes with a verified terminal token.
        if (hasTerminalToken) {
            const extended = decodeLine(atlas, line, {
                startX: x,
                endX: Math.max(options.endX ?? line.estimatedEndX, line.inkX1),
                beamSize: options.beamSize,
                branchLimit: options.branchLimit,
                confidenceFloor: options.confidenceFloor,
                tokenList,
                denylist: options.denylist,
                useOverhangEvidence: options.useOverhangEvidence,
                useBinaryMaskPruning: options.useBinaryMaskPruning,
            });
            if (endedWithTerminalToken(extended) && (!best || quality(extended) > quality(best)))
                best = extended;
        }
    }
    const result = best ?? { text: '', score: -Infinity, x: line.estimatedStartX, decisions: [] };
    return { text: options.trim === false ? result.text : clean(result.text), rawText: result.text, result, line, lineRect, profile: line.profile, atlasVariant: variant };
}
export function decodeTextImage(image, options = {}) {
    if (image.width < 1 || image.height < 1)
        return null;
    const selected = chooseProfile(image, options.profile ?? 'auto', options.lineDetectionThreshold ?? 0.18);
    if (!selected.bounds)
        return null;
    const lineRect = textLineRect(image, selected.bounds);
    const line = buildLineAnchors(cropPixelImage(image, lineRect), {
        profile: selected.profile,
        inkEvidenceThreshold: 0.08,
        endEstimateBasis: options.endEstimateBasis,
    });
    if (line.inkX1 <= line.inkX0 && line.exactX1 <= line.exactX0)
        return null;
    const charset = charsetWithTokens(options.charset ?? CHARSET_PRINTABLE_ASCII, options);
    if (options.atlas)
        return decodeWithAtlas(options.atlas, options.atlasVariant === 'cleartype-bgr' ? 'cleartype-bgr' : 'cleartype-rgb', line, lineRect, options);
    const variant = options.atlasVariant ?? 'auto';
    if (variant !== 'auto')
        return decodeWithAtlas(atlasFor(variant, charset), variant, line, lineRect, options);
    const rgb = decodeWithAtlas(atlasFor('cleartype-rgb', charset), 'cleartype-rgb', line, lineRect, options);
    const bgr = decodeWithAtlas(atlasFor('cleartype-bgr', charset), 'cleartype-bgr', line, lineRect, options);
    return bgr.result.score > rgb.result.score ? bgr : rgb;
}
function uniqueCharset(text) { return [...new Set(text)].join('') || CHARSET_PRINTABLE_ASCII; }
function glyphSequenceScore(line, startX, glyphs) {
    const rendered = composeGlyphSequenceWeights(glyphs);
    let weighted = 0;
    let total = 0;
    for (let y = 0; y < GLYPH_HEIGHT; y++) {
        for (let col = 0; col < rendered.visualWidth; col++) {
            const expected = rendered.weights[y * rendered.visualWidth + col] ?? 0;
            const actual = evidenceAt(line, startX + col, y);
            if (expected < 0.02 && actual < 0.02)
                continue;
            const weight = Math.max(0.1, expected, actual);
            weighted += Math.max(0, 1 - Math.abs(expected - actual)) * weight;
            total += weight;
        }
    }
    return {
        score: weighted,
        normalizedScore: total ? weighted / total : 0,
        endX: startX + rendered.logicalWidth,
        visualEndX: startX + rendered.visualWidth,
    };
}
function matchWithAtlas(atlas, variant, line, lineRect, expected, options) {
    const token = compileDecodeToken(atlas, expected), pad = options.startSearchPad ?? 3;
    const startXs = options.startX === undefined
        ? Array.from({ length: line.estimatedStartX + 1 - Math.max(0, line.estimatedStartX - pad) + 1 }, (_, index) => Math.max(0, line.estimatedStartX - pad) + index)
        : [options.startX];
    let best;
    for (const startX of startXs) {
        if (startX + token.anchorKey.length > line.anchorString.length)
            continue;
        const anchorMatched = line.anchorString.startsWith(token.anchorKey, startX);
        const score = glyphSequenceScore(line, startX, token.glyphs);
        const result = { matches: false, expectedText: expected, score: score.score, normalizedScore: score.normalizedScore, anchorMatched, profile: line.profile, atlasVariant: variant, lineRect, startX, endX: score.endX };
        if (!best || Number(anchorMatched) > Number(best.anchorMatched) || (anchorMatched === best.anchorMatched && result.normalizedScore > best.normalizedScore))
            best = result;
    }
    if (!best)
        return { matches: false, expectedText: expected, score: 0, normalizedScore: 0, anchorMatched: false, reason: 'expected text is wider than the crop' };
    const requireAnchor = options.requireExactAnchorMatch ?? true, minScore = options.minNormalizedScore ?? 0.5;
    best.matches = (!requireAnchor || best.anchorMatched) && best.normalizedScore >= minScore;
    if (!best.matches)
        best.reason = requireAnchor && !best.anchorMatched ? 'anchor columns do not match' : `shape score ${best.normalizedScore.toFixed(3)} is below ${minScore.toFixed(3)}`;
    return best;
}
function better(a, b) {
    if (Number(b.anchorMatched) !== Number(a.anchorMatched))
        return b.anchorMatched ? b : a;
    return b.normalizedScore > a.normalizedScore ? b : a;
}
export function matchTextImage(image, expectedText, options = {}) {
    const expected = options.trim === false ? expectedText : clean(expectedText);
    const selected = chooseProfile(image, options.profile ?? 'auto', options.lineDetectionThreshold ?? 0.18);
    if (!expected)
        return { matches: selected.bounds === null, expectedText: '', score: 0, normalizedScore: selected.bounds ? 0 : 1, anchorMatched: selected.bounds === null };
    if (!selected.bounds)
        return { matches: false, expectedText: expected, score: 0, normalizedScore: 0, anchorMatched: false, reason: 'crop has no text evidence' };
    const lineRect = textLineRect(image, selected.bounds);
    const line = buildLineAnchors(cropPixelImage(image, lineRect), {
        profile: selected.profile,
        inkEvidenceThreshold: 0.08,
        endEstimateBasis: options.endEstimateBasis,
    });
    const charset = options.charset ?? uniqueCharset(expected);
    if (options.atlas)
        return matchWithAtlas(options.atlas, options.atlasVariant === 'cleartype-bgr' ? 'cleartype-bgr' : 'cleartype-rgb', line, lineRect, expected, options);
    const variant = options.atlasVariant ?? 'auto';
    if (variant !== 'auto')
        return matchWithAtlas(atlasFor(variant, charset), variant, line, lineRect, expected, options);
    return better(matchWithAtlas(atlasFor('cleartype-rgb', charset), 'cleartype-rgb', line, lineRect, expected, options), matchWithAtlas(atlasFor('cleartype-bgr', charset), 'cleartype-bgr', line, lineRect, expected, options));
}
export function chooseBestKnownText(image, candidates, options = {}) {
    let best = null;
    for (const candidate of candidates) {
        const result = { candidate, ...matchTextImage(image, candidate, { ...options, requireExactAnchorMatch: options.requireExactAnchorMatch ?? false, minNormalizedScore: options.minNormalizedScore ?? 0 }) };
        if (!best || result.normalizedScore > best.normalizedScore)
            best = result;
    }
    return best;
}
//# sourceMappingURL=text.js.map